<template>
    <BaseModal :model-value="expirationModalVisible" :dismissible="false" exclusive>
        <template #header>
            <h3 class="mb-2">Session expiring</h3>
        </template>
        <template #content>
            <p>Your session is about to expire. Refresh it to remain signed in.</p>
            <p class="mt-4 text-center text-3xl font-semibold tabular-nums">{{ formattedRemainingTime }}</p>
        </template>
        <template #footer>
            <UButton
                label="Refresh"
                size="lg"
                :variant="ButtonVariants.Dark"
                :loading="refreshingSession"
                @click="refreshSession" />
        </template>
    </BaseModal>
</template>

<script setup lang="ts">
    import { useIntervalFn } from '@vueuse/core';
    import { ButtonVariants } from '@constants/button-variants';
    import { EXCLUSIVE_MODAL_OPENED_EVENT, ZERO_STRING } from '@constants/common';

    const SECONDS_PER_MINUTE = 60;
    const EXPIRED_SESSION_REMAINING_SECONDS = 0;
    const TIMER_INTERVAL_MILLISECONDS = 1_000;
    const TIMER_PART_LENGTH = 2;

    const auth = useAuth();
    const route = useRoute();
    const { accessToken, accessTokenExpiration } = storeToRefs(useUserSessionStore());
    const { $refreshAuthToken } = useNuxtApp();

    const currentTimestamp = ref(Date.now());
    const refreshingSession = ref(false);
    const expirationModalOpened = ref(false);

    const remainingSeconds = computed(() => {
        if (!accessTokenExpiration.value) {
            return EXPIRED_SESSION_REMAINING_SECONDS;
        }

        return Math.max(
            Math.ceil((accessTokenExpiration.value.getTime() - currentTimestamp.value) / TIMER_INTERVAL_MILLISECONDS),
            EXPIRED_SESSION_REMAINING_SECONDS
        );
    });

    const formattedRemainingTime = computed(() => {
        const minutes = Math.floor(remainingSeconds.value / SECONDS_PER_MINUTE);
        const seconds = remainingSeconds.value % SECONDS_PER_MINUTE;

        return `${minutes.toString().padStart(TIMER_PART_LENGTH, ZERO_STRING)}:${seconds
            .toString()
            .padStart(TIMER_PART_LENGTH, ZERO_STRING)}`;
    });

    const expirationModalVisible = computed(
        () =>
            !!accessToken.value &&
            remainingSeconds.value > EXPIRED_SESSION_REMAINING_SECONDS &&
            remainingSeconds.value <= 2 * SECONDS_PER_MINUTE
    );

    function updateTimer(timestamp = Date.now()) {
        currentTimestamp.value = timestamp;

        if (expirationModalVisible.value && !expirationModalOpened.value) {
            expirationModalOpened.value = true;

            if (import.meta.client) {
                window.dispatchEvent(new Event(EXCLUSIVE_MODAL_OPENED_EVENT));
            }
        } else if (!expirationModalVisible.value) {
            expirationModalOpened.value = false;
        }

        if (
            accessToken.value &&
            accessTokenExpiration.value &&
            remainingSeconds.value === EXPIRED_SESSION_REMAINING_SECONDS &&
            !refreshingSession.value
        ) {
            void auth.signOut({ revokeRemoteSession: false, redirect: route.fullPath });
        }
    }

    async function refreshSession() {
        if (refreshingSession.value || !accessToken.value) {
            return;
        }

        refreshingSession.value = true;

        try {
            await $refreshAuthToken();
        } finally {
            refreshingSession.value = false;
            updateTimer();
        }
    }

    if (import.meta.client) {
        updateTimer();
    }

    const { pause } = useIntervalFn(updateTimer, TIMER_INTERVAL_MILLISECONDS, { immediate: import.meta.client });

    onScopeDispose(pause);
</script>
